/**
 * Generic Strapi content type registry.
 *
 * Each content type registers:
 *  - `blocks`: maps Strapi dynamic zone component names to the React component
 *    used to render them and the populate params needed to resolve their data.
 *  - `topLevelPopulate`: populate params for top-level fields outside the
 *    dynamic zone (e.g. seo).
 *
 * Consumers (api.ts, StrapiBlock) pass the content type name as a parameter
 * and are therefore not tied to any specific content type.
 */

import type React from 'react';
import {StrapiHeroBlock} from './components/StrapiHeroBlock';
import {StrapiTextMultipleColumnsBlock} from './components/StrapiTextMultipleColumnsBlock';
import {StrapiVideoCarouselBlock} from './components/StrapiVideoCarouselBlock';
import {StrapiBulletsBlock} from './components/StrapiBulletsBlock';
import {StrapiFaqBlock} from './components/StrapiFaqBlock';
import {StrapiHorizontalRailBlock} from './components/StrapiHorizontalRailBlock';
import {StrapiChatAndPostCardBlock} from './components/StrapiChatAndPostCardBlock';
import {StrapiSectionBlock} from './components/StrapiSectionBlock';
import {StrapiHtmlBlock} from './components/StrapiHtmlBlock';
export type {ContentTypePopulateConfig} from './contentTypePopulate';
export {buildPopulateParams} from './contentTypePopulate';

export type BlockConfig = {
	/** React component used to render this block */
	component: React.FC<{strapiBlockData: any; translationNamespace: string}>;
};

export type ContentTypeConfig = {
	/** Dynamic zone block registry for this content type */
	blocks: Record<string, BlockConfig>;
};

const CONTENT_TYPE_CONFIGS: Record<string, ContentTypeConfig> = {
	feature: {
		blocks: {
			'landing.text3-columns': {component: StrapiTextMultipleColumnsBlock},
			'landing.bullets': {component: StrapiBulletsBlock},
			'landing.faq': {component: StrapiFaqBlock},
			'landing.horizontal-rail': {component: StrapiHorizontalRailBlock},
			'landing.hero': {component: StrapiHeroBlock},
			'landing.basic-block': {component: StrapiSectionBlock},
			'landing.carousel': {component: StrapiVideoCarouselBlock},
			'landing.chat-and-post-card': {component: StrapiChatAndPostCardBlock},
			'landing.vanilla-html': {component: StrapiHtmlBlock},
		},
	},
};

/**
 * Returns the React component registered for a given content type block, or
 * null if neither the content type nor the component name is found.
 */
export function getBlockComponent(
	contentType: string,
	componentName: string,
): React.FC<{strapiBlockData: any; translationNamespace: string}> | null {
	return (
		CONTENT_TYPE_CONFIGS[contentType]?.blocks[componentName]?.component ?? null
	);
}
